/**
 * Aligns a local `MemoryPointer` with chain's active memory version
 * via a TTL-cached pointer check. Inside the TTL window: no-op.
 * Past it: one cheap chain query for the current active id; only on
 * mismatch does the full row get refetched and decrypted.
 *
 * Fail modes:
 *   - Chain unreachable → throws the network error verbatim; caller
 *     serves the local copy and retries on the next check.
 *   - Tampered ciphertext (GCM tag mismatch) → throws
 *     `MemoryIntegrityError`. Fail-closed — never silent.
 */
import { getActiveMemoryId, getMemoryById, type AgentMemoryEntry } from "./chain.js";
import type { AgentAuth, ChainOpts } from "../types.js";

/** Per-agent state the caller persists between sync calls. */
export interface MemoryPointer {
  /** Last known chain-active memory id, or null if the agent had no active memory at the last check. */
  activeId: number | null;
  /** Wall-clock ms of the last successful chain check. Use 0 to force a check on the next call. */
  checkedAt: number;
}

export interface SyncMemoryOptions {
  /** Trust the pointer without touching chain for this many ms since `checkedAt`. Default 30_000. */
  ttlMs?: number;
  /** Skip the TTL gate and force a chain check this call. Default false. */
  force?: boolean;
  chainOpts?: ChainOpts;
}

/**
 * `drifted: false` — pointer is still valid; caller can keep serving the local copy.
 * `drifted: true`  — active id changed on chain; `current` is the fresh decrypted row
 *                    (or `null` if active memory was removed entirely).
 */
export type SyncMemoryResult =
  | { drifted: false; pointer: MemoryPointer }
  | { drifted: true; current: AgentMemoryEntry | null; pointer: MemoryPointer };

/** Thrown when a fetched active row fails its GCM integrity check. Poisoning signal — do not swallow. */
export class MemoryIntegrityError extends Error {
  constructor(
    public readonly id: number,
    reason: string,
  ) {
    super(`memory integrity check failed on id ${id}: ${reason}`);
    this.name = "MemoryIntegrityError";
  }
}

const DEFAULT_TTL_MS = 30_000;

export async function syncLocalMemory(
  auth: AgentAuth,
  pointer: MemoryPointer,
  opts: SyncMemoryOptions = {},
): Promise<SyncMemoryResult> {
  const ttl = opts.ttlMs ?? DEFAULT_TTL_MS;
  const now = Date.now();

  const withinTtl = !opts.force && now - pointer.checkedAt < ttl;
  if (withinTtl) {
    return { drifted: false, pointer };
  }

  const currentId = await getActiveMemoryId(auth, opts.chainOpts);
  const nextPointer: MemoryPointer = { activeId: currentId, checkedAt: now };

  if (currentId === pointer.activeId) {
    return { drifted: false, pointer: nextPointer };
  }

  if (currentId === null) {
    return { drifted: true, current: null, pointer: nextPointer };
  }

  const row = await getMemoryById(currentId, auth, opts.chainOpts);
  if (row.decryptError) {
    throw new MemoryIntegrityError(currentId, row.decryptError);
  }
  return { drifted: true, current: row, pointer: nextPointer };
}
